import type { ChannelInfo, Chat, ClientState, Space } from "@rhema/client";
import { isMuted, isUnread } from "../data/view";

/** Whether a space lists a channel: it owns it, or another space shares it with this one. */
export function inSpace(chat: Pick<Chat, "space_id" | "shared_with">, spaceId: string): boolean {
  return chat.space_id === spaceId || Boolean(chat.shared_with?.some((s) => s.id === spaceId));
}

/** The channels you are in for one space, including ones shared with it, in name order. */
export function channelsOf(state: ClientState, spaceId: string): Chat[] {
  return Object.values(state.chats)
    .filter((c) => c.kind === "channel" && inSpace(c, spaceId))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * The space to open a channel in: the one that owns it if you are in that space, or else a space
 * it is shared with that you are in.
 */
export function homeSpaceId(state: ClientState, chat: Chat): string {
  const owner = chat.space_id ?? "";
  if (state.spaces.some((s) => s.id === owner)) return owner;
  return chat.shared_with?.find((s) => state.spaces.some((x) => x.id === s.id))?.id ?? owner;
}

/** How a channel shows that it is shared, seen from one space. Empty when it is not shared. */
export function sharedNote(
  chat: Pick<Chat | ChannelInfo, "space_id" | "space_name" | "shared_with">,
  spaceId: string,
): string {
  if (chat.space_id && chat.space_id !== spaceId) return `Shared from ${chat.space_name || "another space"}`;
  const names = (chat.shared_with ?? []).map((s) => s.name);
  return names.length ? `Shared with ${names.join(", ")}` : "";
}

/** Whether a channel belongs to another space, so this space's admins can only take it out. */
export function isForeign(chat: Pick<Chat | ChannelInfo, "space_id">, spaceId: string): boolean {
  return Boolean(chat.space_id) && chat.space_id !== spaceId;
}

export function spaceOf(state: ClientState, spaceId: string): Space | undefined {
  return state.spaces.find((s) => s.id === spaceId);
}

/** What a space's tile shows: a dot for unread messages, a count for mentions. */
export function spaceActivity(state: ClientState, spaceId: string): { unread: boolean; mentions: number } {
  let unread = false;
  let mentions = 0;
  for (const c of channelsOf(state, spaceId)) {
    if (isMuted(c)) continue;
    if (isUnread(c)) unread = true;
    if (c.mentioned) mentions += 1;
  }
  return { unread, mentions };
}

/** Personal chats: everything that is not a channel in a space. */
export function isPersonal(chat: Chat): boolean {
  return chat.kind !== "channel";
}

export function isAdmin(space: Space | undefined): boolean {
  return space?.role === "owner" || space?.role === "admin";
}

/** A guest is only in the channels they were invited to. */
export function isGuest(space: Space | undefined): boolean {
  return space?.role === "guest";
}

/** Whether someone in a channel is a guest there. */
export function isGuestIn(
  chat: Pick<Chat, "guests"> | undefined,
  userId: string | null | undefined,
): boolean {
  return Boolean(userId && chat?.guests?.includes(userId));
}

/** The link that joins a space. `origin` is the server's, as group invite links use. */
export function spaceInviteLink(code: string, origin: string): string {
  return `${origin}/spaces/join/${code}`;
}

/** The code at the end of a pasted invite link, or the code itself. */
export function inviteCodeFrom(text: string): string {
  const trimmed = text.trim();
  const match = /\/spaces\/join\/([A-Za-z0-9_-]+)/.exec(trimmed);
  return match?.[1] ?? trimmed.replace(/[^A-Za-z0-9_-]/g, "");
}

/** The link an admin of another space opens to add a shared channel there. */
export function shareLink(code: string, origin: string): string {
  return `${origin}/spaces/share/${code}`;
}

export function spaceHref(spaceId: string, chatId?: string): string {
  return chatId ? `/spaces/${spaceId}/${chatId}` : `/spaces/${spaceId}`;
}
